import { useEffect, useMemo, useState } from 'react';

import { Box, Checkbox, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { includes } from '@npi/buddy-utils';
import { useField } from 'formik';

import { Flex } from 'src/components/Flex';
import { FieldInput } from 'src/components/FormikComponents';
import { OptionTypes } from 'src/components/FormikComponents/DropDown';

type ManageElibilityCardType = {
    title?: string;
    asignedToTerminal?: string;
    data?: OptionTypes[];
    name: string;
};

const RADIO_OPTIONS: OptionTypes[] = [
    { label: 'Yes', value: 'YES' },
    { label: 'No', value: 'NO' },
];

const ManageEligibilityCard = ({
    title,
    asignedToTerminal,
    data,
    name,
}: ManageElibilityCardType) => {
    const [{ value: selectedOptions }, _, { setValue: setSelectedOptions }] = useField(name);
    const [{ name: searchName, value: search }, __, { setValue: setSearchValue }] = useField(
        `search-${name}`
    );

    const [assignValues, setAssignValues] = useState(selectedOptions?.length ? 'YES' : 'NO');

    const options = useMemo(() => {
        if (search) {
            return data.filter((el) => {
                return el.label.includes(search);
            });
        }
        return data;
    }, [search, data]);

    useEffect(() => {
        setSearchValue('');

        return () => {
            return setSearchValue(undefined);
        };
    }, []);

    const onChange = (changedValue: string) => {
        if (includes(selectedOptions, changedValue)) {
            selectedOptions
                ? setSelectedOptions(
                      selectedOptions.filter((el) => {
                          return el !== changedValue;
                      })
                  )
                : setSelectedOptions([]);
        } else {
            setSelectedOptions([...selectedOptions, changedValue]);
        }
    };

    return (
        <Flex
            flexDirection="column"
            borderRadius="16px"
            overflow="hidden"
            boxShadow="0px 4px 20px 0px rgba(38, 50, 56, 0.10)"
            border="#F18F34 solid 1px"
            flex={1}
            maxHeight="70vh"
        >
            <Flex bgcolor="#F18F34" padding="16px 34px">
                <Typography color="#fff" fontSize="20px" fontWeight="500" lineHeight="30px">
                    {title}
                </Typography>
            </Flex>
            <Flex padding="16px" flexDirection="column" gap="24px" overflow="auto">
                <Flex flexDirection="column" gap="24px">
                    <Flex alignItems="center">
                        <Typography
                            color="#575757"
                            fontSize="14px"
                            fontWeight="400"
                            lineHeight="21px"
                            marginRight="4px"
                        >
                            {asignedToTerminal}
                        </Typography>
                        <RadioGroup
                            row
                            value={selectedOptions?.length > 0 ? 'YES' : assignValues}
                            onChange={(e) => {
                                setAssignValues(e.target.value);
                                if (e.target.value === 'NO') {
                                    setSelectedOptions([]);
                                }
                            }}
                        >
                            {RADIO_OPTIONS.map(({ label, value: optionValue }) => {
                                return (
                                    <FormControlLabel
                                        key={optionValue}
                                        value={optionValue}
                                        control={<Radio />}
                                        label={label}
                                    />
                                );
                            })}
                        </RadioGroup>
                    </Flex>
                    <Box position="sticky">
                        <FieldInput name={searchName} placeholder="Search" />
                        {options.length > 0 && (
                            <Typography fontSize="10px" marginTop="4px">
                                {`${options.length} results`}
                            </Typography>
                        )}
                    </Box>
                </Flex>
                <Flex flexDirection="column" sx={{ overflowY: 'scroll' }}>
                    {selectedOptions.length > 0 &&
                        selectedOptions.map((el) => {
                            const option = data.find((item) => {
                                return item.value === el;
                            });
                            return (
                                <FormControlLabel
                                    key={`checkbox-${el}`}
                                    control={
                                        <Checkbox
                                            onChange={() => {
                                                return onChange(el);
                                            }}
                                        />
                                    }
                                    label={option.label}
                                    checked
                                />
                            );
                        })}
                    {options.length > 0 &&
                        options
                            .filter((el) => {
                                return !includes(selectedOptions, el.value);
                            })
                            .map((option) => {
                                return (
                                    <FormControlLabel
                                        key={`checkbox-${option.value}`}
                                        value={option.value}
                                        control={
                                            <Checkbox
                                                onChange={() => {
                                                    return onChange(option.value);
                                                }}
                                            />
                                        }
                                        label={option.label}
                                        checked={selectedOptions?.includes(option.value)}
                                    />
                                );
                            })}
                </Flex>
            </Flex>
        </Flex>
    );
};

export default ManageEligibilityCard;
